// LaunchPad.tsx — the authenticated home view.
//
// Lives at route "launchpad". Replaces the "Authenticated: Launch screen"
// JSX block that used to live in Portal.tsx (welcome message + launch cards
// + admin button + sign-out + language toggle). All chrome (sign-out, lang,
// admin) is now in the unified nav; this is just welcome content + quick-
// access cards.

import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthProvider";
import { Flask, ChartBar, Database, Scales } from "@phosphor-icons/react";
import { setRoute, Route } from "./useRoute";

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

type QuickCard = {
  route: Route;
  Icon: React.ComponentType<any>;
  color: string;
  labelKey: string;
  /** Fallback copy for keys not yet in the locale files. */
  fallback?: { en: string; ko: string };
};

const CARDS: QuickCard[] = [
  { route: "app",      Icon: Flask,    color: "#06b6d4", labelKey: "globalNav.app.label" },
  { route: "compare",  Icon: ChartBar, color: "#f59e0b", labelKey: "globalNav.compare.label" },
  {
    route: "goldenratio",
    Icon: Scales,
    color: "#f0abfc",
    labelKey: "globalNav.goldenratio.label",
    fallback: { en: "Golden Ratio", ko: "골든 비율" },
  },
  { route: "database", Icon: Database, color: "#38bdf8", labelKey: "globalNav.database.label" },
];

export default function LaunchPad() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const name: string = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "";

  return (
    <div
      style={{
        minHeight: "calc(100vh - 96px)",
        color: "#f8fafc",
        fontFamily: FONT_STACK,
        position: "relative",
        padding: "32px 24px 80px",
      }}
    >
      <main style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Big shimmering PROTEIN PAL banner */}
        <h1
          style={{
            fontSize: 72,
            fontWeight: 900,
            letterSpacing: "-2px",
            lineHeight: 1,
            textAlign: "center",
            margin: "0 0 24px",
            background:
              "linear-gradient(135deg, #c4b5fd 0%, #f0abfc 25%, #fcd34d 50%, #a78bfa 75%, #c4b5fd 100%)",
            backgroundSize: "300% 300%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "pp-shimmer 4s ease infinite",
          }}
        >
          PROTEIN PAL
        </h1>

        <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 16, margin: "0 0 8px" }}>
          {name
            ? t("launchpad.welcomeNamed", { name, defaultValue: `Welcome back, ${name}.` })
            : t("launchpad.welcome", { defaultValue: "Welcome back." })}
        </p>
        <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, margin: "0 0 48px" }}>
          {t("launchpad.pickATool", { defaultValue: "Pick a tool to get started." })}
        </p>

        {/* Quick-access cards */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 24,
            flexWrap: "wrap",
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {CARDS.map((c) => (
            <QuickCardButton key={c.route} {...c} />
          ))}
        </div>
      </main>

      <style>{`
        @keyframes pp-shimmer {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}

function QuickCardButton({ route, Icon, color, labelKey, fallback }: QuickCard) {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = React.useState(false);
  const lang = i18n.language === "ko" ? "ko" : "en";
  const label = fallback
    ? t(labelKey, { defaultValue: fallback[lang] })
    : t(labelKey);
  return (
    <button
      onClick={() => setRoute(route)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "rgba(15, 11, 42, 0.85)",
        borderRadius: 16,
        padding: "40px 48px",
        width: 240,
        cursor: "pointer",
        border: `1px solid ${hovered ? color : "rgba(139,92,246,0.2)"}`,
        boxShadow: hovered ? `0 15px 40px ${color}44` : "0 10px 30px rgba(0,0,0,0.4)",
        transition: "transform 0.2s, border-color 0.2s, box-shadow 0.2s",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        textAlign: "center",
        backdropFilter: "blur(20px)",
        color: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <Icon size={48} weight="duotone" color={color} />
      <h2
        style={{
          fontSize: 18, fontWeight: 700, margin: 0,
          letterSpacing: 1, textTransform: "uppercase", color: "#f8fafc",
        }}
      >
        {label}
      </h2>
    </button>
  );
}
